"use client";

import React, { useEffect, useRef, useState } from "react";
import styles from "./TechSliderSection.module.css";
import { gameTechData } from "../../data/game-development";

export default function GameTechSlider() {
  const BASE = gameTechData; // using imported data
  const items = [...BASE, ...BASE, ...BASE]; // triplicate for smooth infinite loop

  const trackRef = useRef(null);
  const containerRef = useRef(null);
  const [index, setIndex] = useState(BASE.length);
  const [step, setStep] = useState(0);
  const [paused, setPaused] = useState(false);

  // measure card width to calculate slide distance
  useEffect(() => {
    const measure = () => {
      const track = trackRef.current;
      if (!track) return;
      const card = track.querySelector(`.${styles.card}`);
      if (!card) return;
      const cardW = card.getBoundingClientRect().width;
      setStep(cardW + 18);
    };
    measure();
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // slide movement
  useEffect(() => {
    const track = trackRef.current;
    if (!track || !step) return;
    track.style.transition = "transform .5s ease";
    track.style.transform = `translateX(${-index * step}px)`;
  }, [index, step]);

  // autoplay
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setIndex((i) => i + 1);
    }, 2800);
    return () => clearInterval(id);
  }, [paused]);

  // reset when reaching cloned edges
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onEnd = () => {
      const total = items.length;
      if (index >= total - BASE.length) {
        const target = index - BASE.length;
        track.style.transition = "none";
        track.style.transform = `translateX(${-target * step}px)`;
        setTimeout(() => {
          track.style.transition = "transform .5s ease";
          setIndex(target);
        }, 0);
      } else if (index < BASE.length) {
        const target = index + BASE.length;
        track.style.transition = "none";
        track.style.transform = `translateX(${-target * step}px)`;
        setTimeout(() => {
          track.style.transition = "transform .5s ease";
          setIndex(target);
        }, 0);
      }
    };
    track.addEventListener("transitionend", onEnd);
    return () => track.removeEventListener("transitionend", onEnd);
  }, [index, step, items.length, BASE.length]);

  const activeDot = ((index % BASE.length) + BASE.length) % BASE.length;

  return (
    <section className={`py-5 ${styles.section}`}>
      <div className="container">
        <div className={styles.head}>
          <div className={styles.eyebrow}>Game Technology</div>
          <h2 className={styles.title}>
            Cutting-Edge Technologies Powering Our Game Development
          </h2>
          <p className={styles.subtitle}>
            As pioneers in game development, we integrate leading engines,
            languages, and frameworks to achieve unmatched quality.
          </p>
        </div>

        <div
          ref={containerRef}
          className={styles.viewport}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div
            ref={trackRef}
            className={styles.track}
            role="list"
            aria-label="Game technologies carousel"
          >
            {items.map((it, idx) => (
              <article
                key={`${it.label}-${idx}`}
                className={styles.card}
                role="listitem"
              >
                <div
                  className={styles.badge}
                  style={{
                    background: it.color,
                    borderColor: it.color,
                  }}
                >
                  <span className={styles.logo} style={{ color: it.color }}>
                    <i className={it.icon} aria-hidden></i>
                  </span>
                  <span className={styles.badgeLabel}>{it.label}</span>
                </div>
                <p className={styles.desc}>{it.text}</p>
              </article>
            ))}
          </div>
        </div>

        <div className={styles.dots} aria-hidden>
          {BASE.map((_, i) => (
            <span
              key={i}
              className={`${styles.dot} ${
                activeDot === i ? styles.dotActive : ""
              }`}
            ></span>
          ))}
        </div>
      </div>
    </section>
  );
}
